import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Chip,
  Grid,
  MenuItem,
  IconButton,
  Skeleton,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import ConfirmDialog from '../components/ConfirmDialog';
import ActionButton from '../components/ActionButton';
import { showSuccess, showError } from '../utils/notifications';

const MotionBox = motion(Box);

function Units() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [systemFilter, setSystemFilter] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, id: null });
  const [formData, setFormData] = useState({
    unit_code: '',
    unit_name: '',
    unit_symbol: '',
    unit_type: '',
    unit_system: '',
    conversion_factor: '',
    base_unit_id: '',
    description: '',
    is_active: true,
  });

  const fetchUnits = async () => {
    try {
      setLoading(true);
      const response = await API.units.list();
      setUnits(response?.data || []);
    } catch (err) {
      showError('Failed to load units');
      console.error('Units fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  const handleOpenDialog = (unit = null) => {
    if (unit) {
      setEditingUnit(unit);
      setFormData({
        unit_code: unit.unit_code || '',
        unit_name: unit.unit_name || '',
        unit_symbol: unit.unit_symbol || '',
        unit_type: unit.unit_type || '',
        unit_system: unit.unit_system || '',
        conversion_factor: unit.conversion_factor || '',
        base_unit_id: unit.base_unit_id || '',
        description: unit.description || '',
        is_active: unit.is_active !== false,
      });
    } else {
      setEditingUnit(null);
      setFormData({
        unit_code: '',
        unit_name: '',
        unit_symbol: '',
        unit_type: '',
        unit_system: '',
        conversion_factor: '',
        base_unit_id: '',
        description: '',
        is_active: true,
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingUnit(null);
    setFormData({
      unit_code: '',
      unit_name: '',
      unit_symbol: '',
      unit_type: '',
      unit_system: '',
      conversion_factor: '',
      base_unit_id: '',
      description: '',
      is_active: true,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingUnit) {
        await API.units.update(editingUnit.unit_id, formData);
        showSuccess('Unit updated successfully!');
      } else {
        await API.units.create(formData);
        showSuccess('Unit created successfully!');
      }
      handleCloseDialog();
      fetchUnits();
    } catch (err) {
      const errorMessage = editingUnit 
        ? 'Failed to update unit' 
        : 'Failed to create unit';
      showError(errorMessage);
      console.error('Unit submit error:', err);
    }
  };

  const handleDelete = async (unitId) => {
    setConfirmDialog({ open: true, id: unitId });
  };

  const handleConfirmDelete = async () => {
    try {
      await API.units.deactivate(confirmDialog.id);
      showSuccess('Unit deleted successfully!');
      fetchUnits();
    } catch (err) {
      showError('Failed to delete unit');
      console.error('Unit delete error:', err);
    } finally {
      setConfirmDialog({ open: false, id: null });
    }
  };

  const filteredUnits = units.filter(unit => {
    const matchesSearch = unit.unit_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.unit_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      unit.unit_type?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = !typeFilter || unit.unit_type === typeFilter;
    const matchesSystem = !systemFilter || unit.unit_system === systemFilter;
    
    return matchesSearch && matchesType && matchesSystem;
  });

  const columns = [
    { 
      field: 'unit_name', 
      headerName: 'Unit Name', 
      width: 180,
      render: (row) => row.unit_name 
    },
    { 
      field: 'unit_code', 
      headerName: 'Unit Code', 
      width: 130,
      render: (row) => row.unit_code 
    },
    { 
      field: 'unit_symbol', 
      headerName: 'Symbol', 
      width: 100,
      render: (row) => row.unit_symbol || '-'
    },
    { 
      field: 'unit_type', 
      headerName: 'Type', 
      width: 150,
      render: (row) => row.unit_type 
    },
    { 
      field: 'unit_system', 
      headerName: 'System', 
      width: 150,
      render: (row) => row.unit_system || '-'
    },
    { 
      field: 'is_active', 
      headerName: 'Status', 
      width: 120,
      render: (row) => (
        <Chip 
          label={row.is_active ? 'Active' : 'Inactive'} 
          color={row.is_active ? 'success' : 'default'}
          size="small" 
        />
      )
    },
  ];

  const filterFields = [
    {
      name: 'type',
      label: 'Unit Type',
      value: typeFilter,
      onChange: setTypeFilter,
      options: [
        { value: '', label: 'All Types' },
        ...Array.from(new Set(units.map(u => u.unit_type).filter(Boolean))).map(type => ({
          value: type,
          label: type,
        })),
      ],
    },
    {
      name: 'system',
      label: 'Unit System',
      value: systemFilter,
      onChange: setSystemFilter,
      options: [
        { value: '', label: 'All Systems' },
        { value: 'SI', label: 'SI' },
        { value: 'Imperial', label: 'Imperial' },
        { value: 'US Customary', label: 'US Customary' },
        { value: 'Other', label: 'Other' },
      ],
    },
  ];

  const TableSkeleton = () => (
    <Box sx={{ width: '100%' }}>
      {[...Array(5)].map((_, i) => (
        <Skeleton key={i} height={60} sx={{ my: 1 }} />
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Units
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} disabled>
            Add Unit
          </Button>
        </Box>
        <TableSkeleton />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Units
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Unit
        </Button>
      </Box>

      <FilterBar 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterFields={filterFields}
      />

      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <DataTable
          columns={columns}
          data={filteredUnits}
          onEdit={handleOpenDialog}
          onDelete={handleDelete}
          idField="unit_id"
        />
      </MotionBox>

      <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {editingUnit ? 'Edit Unit' : 'Add New Unit'}
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit Name"
                value={formData.unit_name}
                onChange={(e) => setFormData({ ...formData, unit_name: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit Code"
                value={formData.unit_code}
                onChange={(e) => setFormData({ ...formData, unit_code: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit Symbol"
                value={formData.unit_symbol}
                onChange={(e) => setFormData({ ...formData, unit_symbol: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unit Type"
                value={formData.unit_type}
                onChange={(e) => setFormData({ ...formData, unit_type: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Unit System"
                value={formData.unit_system}
                onChange={(e) => setFormData({ ...formData, unit_system: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value="SI">SI (International System)</MenuItem>
                <MenuItem value="Imperial">Imperial</MenuItem>
                <MenuItem value="US Customary">US Customary</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Conversion Factor"
                type="number"
                value={formData.conversion_factor}
                onChange={(e) => setFormData({ ...formData, conversion_factor: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <ActionButton variant="outlined" onClick={handleCloseDialog}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" onClick={handleSubmit}>
            {editingUnit ? 'Update' : 'Create'}
          </ActionButton>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Delete Unit"
        message="Are you sure you want to delete this unit? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDialog({ open: false, id: null })}
      />
    </Box>
  );
}

export default Units;